import {
    AbstractControl,
    AsyncValidatorFn,
    FormGroup,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    first,
    startWith,
    switchMap
} from 'rxjs/operators';
import { ZodType } from 'zod';

const htmlRegex = /<\/?[a-z][\s\S]*>/i;

/**
 * A validator that will return a validation error if any text in the form control's value seems to contain HTML when checked against a REGEX.
 * @returns a notHtml error if the text seems to contain html code, or null if not.
 */
export function htmlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (htmlRegex.test(control.value)) {
            return { notHtml: 'Input cannot contain HTML code' };
        } else {
            return null;
        }
    };
}

/**
 * A Fornmly-compatible validator that will return a validation error if any text in the form control's value seems to contain HTML when checked against a REGEX.
 * @returns a notHtml error if the text seems to contain html code, or null if not.
 */
export function formlyHtmlValidator(
    control: AbstractControl
): ValidationErrors {
    if (htmlRegex.test(control.value)) {
        return { notHtml: true };
    } else {
        return null;
    }
}

/**
 * A form control validator for JSON. Generally to be used to validate JSON entered as free text. Can optionally validate the JSON against a provided schema and return relevant errors
 * @param zodSchema A Zod schema defining the expected shape for the JSON
 * @returns Validation errors using keys that the validation service can also check against.
 */
export function jsonValidator(zodSchema?: ZodType): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (control.value == null || control.value === '') {
            // Use Validators.required separately if a value is mandatory.
            return null;
        }
        try {
            const controlValue = JSON.parse(control.value);
            if (controlValue != null && zodSchema != null) {
                const valid = zodSchema.safeParse(controlValue);
                if (valid.success) {
                    return null;
                } else {
                    return { jsonSchema: 'JSON does not match expected shape' };
                }
            }
            return null;
        } catch {
            return { json: 'provided JSON is not valid' };
        }
    };
}

// FIXME: this currently won't work. It only validates correctly after the field's value has been changed
/**
 * An async form control validator for JSON. Generally to be used to validate JSON entered as free text. Can optionally validate the JSON against a provided schema and return relevant errors
 * @param zodSchema A Zod schema defining the expected shape for the JSON
 * @returns An observable with Validation errors using keys that the validation service can also check against.
 */
export function asyncJsonValidator(zodSchema?: ZodType): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> =>
        control.valueChanges.pipe(
            startWith(control.value), // Needed to set correct validation when the form values are loaded in
            debounceTime(200),
            distinctUntilChanged(),
            switchMap(value => {
                try {
                    const controlValue = JSON.parse(value);
                    if (controlValue != null && zodSchema != null) {
                        const result = zodSchema.safeParse(controlValue);
                        if (result.success) {
                            return of(null);
                        } else {
                            return of({
                                jsonSchema: 'JSON does not match expected shape'
                            });
                        }
                    }
                    return of(null);
                } catch {
                    return of({ json: 'provided JSON is not valid' });
                }
            }),
            first() // important to make observable finite
        );
}

/**
 * A validator that checks if an array is empty.
 * @returns a notEmptyArray error if the array is empty, or null if not.
 */
export function notEmptyArrayValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (
            control.value &&
            Array.isArray(control.value) &&
            control.value.length === 0
        ) {
            return { notEmptyArray: 'Array cannot be empty' };
        }
        return null;
    };
}

export function endAfterStartValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.parent) return null;
    const parentForm = control.parent as FormGroup;

    if (!(control as any).__endAfterStartSubscribed) {
        (control as any).__endAfterStartSubscribed = true;
        parentForm.get('startTime')?.valueChanges.subscribe(() => {
            control.updateValueAndValidity({ emitEvent: false });
        });
    }

    const endTime = control.value;
    if (!endTime) return null;

    const startTime = parentForm.get('startTime')?.value;
    if (!startTime) return null;

    return moment(endTime).isBefore(moment(startTime))
        ? { endBeforeStart: { message: 'End time cannot be before start time' } }
        : null;
}

/**
 * A validator that ensures email uniqueness by preventing case-sensitivity bypass.
 * This validator normalizes email addresses to lowercase for comparison, preventing users
 * from creating duplicate accounts by altering the case of email addresses.
 * For example: alice@example.com and Alice@example.com will be treated as the same email.
 *
 * @param existingEmails Array of existing email addresses to check against for duplicates
 * @returns ValidationErrors with 'emailExists' error if email already exists, null otherwise
 */
export function uniqueEmailValidator(existingEmails: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const normalizedEmail = control.value.toLowerCase().trim();
        const emailExists = existingEmails.some(
            email => email.toLowerCase().trim() === normalizedEmail
        );
        return emailExists
            ? { emailExists: 'This email address is already in use' }
            : null;
    };
}
